import { get, post, patch, del } from '../utils/apiClient'

// Service du module Apport d'affaires (business_referrals). Auth = cookie
// httpOnly (withCredentials géré par apiClient). get() renvoie déjà data.
// Étendu au fil des lots front (F2 feed ; F3 ROI membre ; F4 dashboard admin).
class BusinessReferralService {
  constructor() {
    this.baseUrl = '/business-referrals'
  }

  // F2 — Fil de gratitude (referrals confirmés, sans montant ni client).
  async getFeed({ page = 1, limit = 20 } = {}) {
    return get(`${this.baseUrl}/feed`, { params: { page, limit } })
  }

  // F3 — ROI nominatif du membre courant (leading/lagging, reçu/généré).
  async getMyRoi() {
    return get(`${this.baseUrl}/me/roi`)
  }

  // Impact réseau public d'un membre (Lot 1) — métriques autorisées uniquement
  // (générosité + niveau + activité coarse). Reçus/montants NON exposés ici.
  async getMemberNetworkImpact(memberId) {
    return get(`${this.baseUrl}/members/${memberId}/network-impact`)
  }

  // Scorecard membre (Lot 3, admin) — volumes de pilotage + dormance, aucun
  // montant. Route protégée par checkRole côté backend.
  async getMemberScorecard(memberId) {
    return get(`${this.baseUrl}/admin/members/${memberId}/scorecard`)
  }

  // F4 — Agrégats club (admin). Route protégée par checkRole côté backend.
  async getAdminRoi() {
    return get(`${this.baseUrl}/admin/roi`)
  }

  // Tableau Président (Lot 1a/1b) — analytics temporelles + funnel + secteurs +
  // entreprises. Route protégée par checkRole. Le controller enveloppe dans
  // { success, data } → on renvoie directement le payload `data`.
  async getAnalytics({ period = '30d' } = {}) {
    const res = await get(`${this.baseUrl}/admin/analytics`, { params: { period } })
    return res?.data ?? null
  }

  // Radar d'assiduité/engagement (A3). { coldStart, counters, members, reliableCount… }.
  async getMemberHealth() {
    const res = await get(`${this.baseUrl}/admin/member-health`)
    return res?.data ?? null
  }

  // P1a — Signaux membres + santé interprétée (matrice président). Contrairement
  // à getAnalytics, le controller renvoie le rapport À PLAT (success + version +
  // windowDays + thresholds + summary + members), PAS sous `data`. On retire
  // `success` et on renvoie le rapport métier seul (null si échec applicatif).
  // AUCUN recalcul ici : la santé vient telle quelle de deriveMemberHealth.
  async getMemberSignals() {
    const res = await get(`${this.baseUrl}/admin/member-signals`)
    if (!res?.success) return null
    const { success, ...report } = res
    return report
  }

  // P1b — Nudges copilote (président). Même forme À PLAT que member-signals :
  // { success, version, windowDays, summary, nudges }. On retire `success`.
  async getNudges() {
    const res = await get(`${this.baseUrl}/admin/nudges`)
    if (!res?.success) return null
    const { success, ...report } = res
    return report
  }

  // P1b — Reporter (snooze) un nudge : durées bornées 7/30/90j. Le backend valide
  // (code catalogue, durée, nudge actif) et résout l'admin serveur.
  async snoozeNudge(code, { subjectMemberId, durationDays }) {
    return post(`${this.baseUrl}/admin/nudges/${encodeURIComponent(code)}/snooze`, {
      subjectMemberId,
      durationDays,
    })
  }

  // P1d — Reports ACTIFS de l'admin courant (non expirés), avec l'identité du
  // membre et `isNudgeCurrentlyActive`. Privés : le backend cloisonne par admin.
  async getNudgeSnoozes() {
    const res = await get(`${this.baseUrl}/admin/nudge-snoozes`)
    if (!res?.success) return null
    return Array.isArray(res.snoozes) ? res.snoozes : []
  }

  // P1d — « Réafficher maintenant » : suppression anticipée du report. Cible
  // ENTIÈREMENT dans le chemin (aucun body sur un DELETE).
  async deleteNudgeSnooze(code, subjectMemberId) {
    return del(
      `${this.baseUrl}/admin/nudges/${encodeURIComponent(code)}/snooze/${encodeURIComponent(subjectMemberId)}`,
    )
  }

  // F5a — Mes apports (referrals où je suis provider ou recipient).
  async listReferrals({ page = 1, limit = 20 } = {}) {
    return get(`${this.baseUrl}/referrals`, { params: { page, limit } })
  }

  // F5a — Remercier : le controller force recipient = utilisateur courant.
  async createReferral({ providerMemberId, clientCompanyName, introductionId } = {}) {
    return post(`${this.baseUrl}/referrals`, {
      providerMemberId,
      clientCompanyName,
      introductionId,
    })
  }

  // F5a — Ajouter un résultat à un apport (non atomique avec createReferral).
  async createOutcome(referralId, payload) {
    return post(`${this.baseUrl}/referrals/${referralId}/outcomes`, payload)
  }

  // F5b — Faire progresser le stade (recipient uniquement, forward-only backend).
  async updateStage(referralId, toStage) {
    return patch(`${this.baseUrl}/referrals/${referralId}/stage`, { toStage })
  }

  // F5b — Résultats d'un apport (montants visibles car viewer = partie).
  async listOutcomes(referralId) {
    return get(`${this.baseUrl}/referrals/${referralId}/outcomes`)
  }

  // F5b — Modifier un résultat (recipient, statut pending uniquement côté backend).
  async updateOutcome(outcomeId, payload) {
    return patch(`${this.baseUrl}/outcomes/${outcomeId}`, payload)
  }

  // F5c — Un apport (pour réaligner l'UI après un conflit de transition).
  async getReferral(referralId) {
    return get(`${this.baseUrl}/referrals/${referralId}`)
  }

  // F5c — Confirmer/contester un apport (provider uniquement, motif requis pour dispute).
  async confirmReferral(referralId) {
    return post(`${this.baseUrl}/referrals/${referralId}/confirm`)
  }

  async disputeReferral(referralId, reason) {
    return post(`${this.baseUrl}/referrals/${referralId}/dispute`, { reason })
  }

  // F5c — Confirmer/contester un résultat (provider uniquement, motif requis pour dispute).
  async confirmOutcome(outcomeId) {
    return post(`${this.baseUrl}/outcomes/${outcomeId}/confirm`)
  }

  async disputeOutcome(outcomeId, reason) {
    return post(`${this.baseUrl}/outcomes/${outcomeId}/dispute`, { reason })
  }

  // F6.1 — Arbitrage admin. File des litiges (résumé sans montant).
  async getDisputes({ type = 'all', page = 1, limit = 20 } = {}) {
    return get(`${this.baseUrl}/admin/disputes`, { params: { type, page, limit } })
  }

  // F6.1 — Contexte d'un litige d'outcome (montants visibles à l'admin, §7.3).
  async getOutcomeDisputeContext(outcomeId) {
    return get(`${this.baseUrl}/admin/outcomes/${outcomeId}/dispute-context`)
  }

  // F6.1 — Trancher un litige (admin). decision = 'confirmed' | 'rejected'.
  async resolveReferral(referralId, decision) {
    return post(`${this.baseUrl}/referrals/${referralId}/resolve`, { decision })
  }

  async resolveOutcome(outcomeId, decision) {
    return post(`${this.baseUrl}/outcomes/${outcomeId}/resolve`, { decision })
  }
}

export default new BusinessReferralService()
